import { NotificationsManager } from "@/components/root/navbar/notifications/NotificationManager";
import * as api from "./api";
import { AppScript } from "./models";
import { DownloadService } from "@/common/download.service";
import { SharedState } from "@/common/state";
import { emptyFn } from "@/fp";

export const ScriptJobManager = (() => {
  const jobs_result_bundle: Record<
    string,
    {
      listener: (full_script_status: string, items: any[]) => void;
      script: AppScript;
      entities: Array<{ file_name: string; download_fn: () => void }>;
      waiting_for_count: number;
    }
  > = {};

  const create_notification = (payload: {
    powershell: {
      include: boolean;
      script: string;
    };
    action_name: string;
    readme: string;
    script_job_hash: string;
    job_id: string;
    file_name: string;
  }) => {
    const {
      file_name,
      job_id,
      script_job_hash,
      powershell,
      readme,
      action_name,
    } = payload;
    NotificationsManager.addPollTask({
      type: "poll",
      id: job_id,
      group: "scripts" + script_job_hash,
      icon: "CSV",
      name: action_name,
      retryCount: 1,
      canBeRemovedBeforeFinish: false,
      status: "in-progress",
      exec: async (notif) => {
        const job_ids = notif.id.split(",");
        const res = await Promise.all(
          job_ids.map((id) => api.check_get_entities_job_status(id))
        );
        console.log("CHECKING STATUS AGAIN FOR JOB :", notif.id, res);
        if (res.every((d) => d.success)) {
          return {
            ...notif,
            status: res.every((d) => d.success && d.value.status === "done")
              ? "done"
              : "in-progress",
          };
        } else {
          throw 500;
        }
      },
      onCancel: async () => {
        delete jobs_result_bundle[script_job_hash];
      },
      onDone: async (notif) => {
        const job_ids = notif.id.split(",");
        const download_fns = job_ids.map((job_id) => {
          const [, download] = DownloadService.from_web.csv({
            authorization: SharedState.loggedUser.value.authorization,
            fileName: file_name,
            filePath: `${SharedState.env.value.server}/script/job/${job_id}`,
          });
          return download;
        });

        const script_results = jobs_result_bundle[script_job_hash];
        delete jobs_result_bundle[script_job_hash];
        if (script_results) {
          const {
            entities,
            script,
            listener,
            waiting_for_count,
          } = script_results;
          entities.push({
            download_fn: () => {
              download_fns.forEach((download) => download());
              NotificationsManager.remove(notif.id);
            },
            file_name,
          });
          if (entities.length === waiting_for_count) {
            const items = [...entities];
            if (powershell.include) {
              items.push({
                file_name: `${action_name}.ps1`,
                download_fn: () => {
                  DownloadService.from_str(script.script, `${action_name}.ps1`);
                  NotificationsManager.remove(script_job_hash + "_script");
                },
              });
              items.push({
                file_name: `UNDO_${action_name}.ps1`,
                download_fn: () => {
                  DownloadService.from_str(script.script, `${action_name}.ps1`);
                  NotificationsManager.remove(script_job_hash + "_script");
                },
              });
              items.push({
                file_name: "README.txt",
                download_fn: () => {
                  DownloadService.from_str(script.readme, "README.txt");
                  NotificationsManager.remove(script_job_hash + "_script");
                },
              });
            }
            listener && listener("done", items);
          }
        }
      },
      actions: [
        {
          name: "Download",
          icon: "Download",
          action: async (notification) => {
            const job_ids = notification.id.split(",");
            const download_fns = job_ids.map((job_id) => {
              const [, download] = DownloadService.from_web.csv({
                authorization: SharedState.loggedUser.value.authorization,
                fileName: file_name,
                filePath: `${SharedState.env.value.server}/script/job/${job_id}`,
              });
              return download;
            });
            download_fns.forEach((download) => download());
            DownloadService.from_str(readme, "README.txt");
            if (powershell.include) {
              DownloadService.from_str(powershell.script, `${action_name}.ps1`);
              DownloadService.from_str(
                powershell.script,
                `UNDO_${action_name}.ps1`
              );
            }
            NotificationsManager.remove(notification.id);
          },
        },
      ],
    });
  };

  const vars_to_file_name = (vars: Record<string, string>): string => {
    return (
      Object.values(vars).reduce((acc, val, index) => {
        const type = SharedState.getInfoType(val);
        acc += (index > 0 ? "_" : "") + (type ? type.labelName : val);
        return acc;
      }, "") + ".csv"
    );
  };

  const create_job = async (
    script: AppScript,
    vars: Record<string, string>,
    listener: (full_script_status: string, items: any[]) => void
  ): Promise<[boolean, () => void]> => {
    const hash = [script.id, ...Object.entries(vars).flat()].sort().join("__");

    if (jobs_result_bundle[hash]) {
      jobs_result_bundle[hash].listener = listener;
      return [
        true,
        (): void => {
          if (jobs_result_bundle[hash]) {
            jobs_result_bundle[hash].listener = emptyFn();
          }
        },
      ];
    }

    jobs_result_bundle[hash] = {
      listener,
      entities: [],
      script,
      waiting_for_count: 1,
    };

    console.log("VARS:", vars);
    const all_vars = script.predefined_vars.reduce((acc, str) => {
      const [k, v] = str.split(":");
      acc[k] = v;
      return acc;
    }, vars);

    const res = await api.create_get_entities_job(all_vars);

    if (!res.success) {
      return [false, emptyFn()];
    }

    create_notification({
      readme: script.readme,
      powershell: {
        include: script.type !== "cloud",
        script: script.script,
      },
      action_name: script.action,
      script_job_hash: hash,
      job_id: res.value.job_ids.join(","),
      file_name: vars_to_file_name(vars),
    });

    return [
      true,
      (): void => {
        if (jobs_result_bundle[hash]) {
          jobs_result_bundle[hash].listener = emptyFn();
        }
      },
    ];
  };

  return {
    create_job,
  };
})();
